import { useCallback, useEffect, useMemo, useState } from "react";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import {
  Alert, Box, Button, Card, CardContent, Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle, Divider, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { getApiError } from "../api/errors";
import {
  cancelPurchaseOrder, getPurchaseOrder, markPurchaseOrderOrdered, replacePurchaseOrderDraft,
} from "../api/purchaseOrders";
import PurchaseOrderStatusChip from "../components/PurchaseOrderStatusChip";
import { useAuth } from "../context/AuthContext";
import MainLayout from "../layouts/MainLayout";
import { useAIPageContext } from "../context/AIAssistantContext";

const CANCELLABLE = new Set(["DRAFT", "ORDERED", "PARTIALLY_RECEIVED"]);
const sum = (items, field) => items.reduce((total, item) => total + Number(item[field] || 0), 0);

export default function PurchaseOrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { selectedStore, selectedWorkspace } = useAuth();
  const [order, setOrder] = useState(null);
  const [supplier, setSupplier] = useState("");
  const [lines, setLines] = useState([]);
  const [dialog, setDialog] = useState("");
  const [cancelReason, setCancelReason] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const focusAIAction = useAIPageContext({ version: 1, page_id: "purchase_orders.detail", entity_type: "purchase_order", entity_public_id: id, section: "items" });

  const amount = useCallback((value) => Number(value).toLocaleString(i18n.language, {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  }), [i18n.language]);
  const date = (value) => value ? new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium" }).format(new Date(value)) : "—";

  const applyOrder = useCallback((next) => {
    setOrder(next);
    setSupplier(next.supplier_name || "");
    setLines((next.items || []).map((item) => ({ ...item, ordered_quantity: String(item.ordered_quantity) })));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await getPurchaseOrder(id);
      applyOrder(response.data);
    } catch (requestError) {
      setError(getApiError(requestError, t("purchaseOrders.loadOneError")));
    } finally {
      setLoading(false);
    }
  }, [applyOrder, id, t]);

  useEffect(() => { load(); }, [load, selectedStore?.public_id, selectedWorkspace?.public_id]);

  const summary = useMemo(() => {
    const known = lines.filter((line) => line.expected_unit_cost !== null && line.expected_unit_cost !== undefined);
    return {
      knownCost: known.reduce((total, line) => total + Number(line.expected_unit_cost) * Number(line.ordered_quantity), 0),
      unknownCost: lines.length - known.length,
      ordered: sum(lines, "ordered_quantity"), received: sum(lines, "received_quantity"), outstanding: sum(lines, "outstanding_quantity"),
    };
  }, [lines]);

  const variant = (line) => line.variant_label || (line.variant_options
    ? Object.values(line.variant_options).join(" / ") : "—");

  const save = async () => {
    if (!lines.length || lines.some((line) => !Number.isInteger(Number(line.ordered_quantity)) || Number(line.ordered_quantity) < 1)) {
      setError(t("purchaseOrders.quantityError"));
      return;
    }
    setBusy(true); setError(""); setNotice("");
    try {
      await replacePurchaseOrderDraft(id, {
        supplier_name: supplier.trim() || null,
        items: lines.map((line) => ({
          product_public_id: line.product_public_id,
          product_variant_public_id: line.product_variant_public_id || null,
          ordered_quantity: Number(line.ordered_quantity),
          expected_unit_cost: line.expected_unit_cost,
          expected_cost_source: line.expected_cost_source,
          recommendation_reason: line.recommendation_reason,
        })),
      });
      await load();
      setNotice(t("purchaseOrders.saved"));
    } catch (requestError) {
      setError(getApiError(requestError, t("purchaseOrders.saveError")));
    } finally { setBusy(false); }
  };

  const transition = async () => {
    setBusy(true); setError(""); setNotice("");
    try {
      if (dialog === "order") {
        await markPurchaseOrderOrdered(id);
        setNotice(t("purchaseOrders.orderedSuccess"));
      } else {
        await cancelPurchaseOrder(id, cancelReason);
        setNotice(t("purchaseOrders.cancelledSuccess"));
      }
      setDialog(""); setCancelReason("");
      await load();
    } catch (requestError) {
      setError(getApiError(requestError, t("purchaseOrders.transitionError")));
    } finally { setBusy(false); }
  };

  if (loading) return <MainLayout><Typography color="text.secondary">{t("common.loading")}</Typography></MainLayout>;
  if (!order) return <MainLayout><Button startIcon={<ArrowBackRoundedIcon />} onClick={() => navigate("/purchase-orders")}>{t("purchaseOrders.back")}</Button>{error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}</MainLayout>;

  const editable = order.status === "DRAFT";
  return (
    <MainLayout>
      <Button startIcon={<ArrowBackRoundedIcon />} onClick={() => navigate("/purchase-orders")} sx={{ mb: 2 }}>{t("purchaseOrders.back")}</Button>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {notice && <Alert severity="success" sx={{ mb: 2 }}>{notice}</Alert>}
      <Card><CardContent>
        <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ sm: "center" }} gap={1}>
          <Box><Typography variant="h4" fontWeight={900}>{order.order_number}</Typography><Typography color="text.secondary">{t("purchaseOrders.detailSubtitle")}</Typography></Box>
          <PurchaseOrderStatusChip status={order.status} />
        </Stack>

        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(4, 1fr)" }, gap: 1.5, my: 3 }}>
          {[
            [t("purchaseOrders.created"), date(order.created_at)], [t("purchaseOrders.ordered"), date(order.ordered_at)],
            [t("purchaseOrders.createdBy"), order.created_by_user_id ?? "—"], [t("purchaseOrders.supplier"), order.supplier_name || "—"],
          ].map(([label, value]) => <Box key={label} sx={{ p: 1.5, bgcolor: "action.hover", borderRadius: 2 }}><Typography variant="caption" color="text.secondary">{label}</Typography><Typography fontWeight={800} sx={{ overflowWrap: "anywhere" }}>{value}</Typography></Box>)}
        </Box>
        {order.status === "CANCELLED" && <Alert severity="warning" sx={{ mb: 2 }}>{t("purchaseOrders.cancelledAt", { date: date(order.cancelled_at), reason: order.cancellation_reason || "—" })}</Alert>}

        <Box component="section" aria-label={t("purchaseOrders.summaryLabel")} sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, 1fr)", md: "repeat(3, 1fr)", lg: "repeat(6, 1fr)" }, gap: 1.25, mb: 3 }}>
          {[
            [t("purchaseOrders.lineCount"), lines.length], [t("purchaseOrders.knownCost"), amount(summary.knownCost)],
            [t("purchaseOrders.unknownCostCount"), summary.unknownCost], [t("purchaseOrders.totalOrdered"), summary.ordered],
            [t("purchaseOrders.totalReceived"), summary.received], [t("purchaseOrders.totalOutstanding"), summary.outstanding],
          ].map(([label, value]) => <Box key={label} sx={{ p: 1.5, border: "1px solid", borderColor: "divider", borderRadius: 2 }}><Typography variant="caption" color="text.secondary">{label}</Typography><Typography fontWeight={900}>{value}</Typography></Box>)}
        </Box>

        {editable && <TextField fullWidth label={t("purchaseOrders.supplier")} value={supplier} onChange={(event) => setSupplier(event.target.value)} sx={{ mb: 2 }} />}
        <TableContainer sx={{ display: { xs: "none", md: "block" } }}>
          <Table aria-label={t("purchaseOrders.itemsTableLabel")} size="small"><TableHead><TableRow>
            <TableCell>{t("common.product")}</TableCell><TableCell>{t("common.variant")}</TableCell>
            <TableCell align="right">{t("purchaseOrders.orderedQuantity")}</TableCell><TableCell align="right">{t("purchaseOrders.unitCost")}</TableCell>
            <TableCell>{t("purchaseOrders.costSource")}</TableCell><TableCell align="right">{t("purchaseOrders.receivedQuantity")}</TableCell>
            <TableCell align="right">{t("purchaseOrders.outstandingQuantity")}</TableCell><TableCell>{t("purchaseOrders.reason")}</TableCell>
            {editable && <TableCell>{t("common.actions")}</TableCell>}
          </TableRow></TableHead><TableBody>{lines.map((line, index) => <TableRow key={line.public_id}>
            <TableCell>{line.product_name}</TableCell><TableCell>{variant(line)}</TableCell>
            <TableCell align="right">{editable ? <TextField size="small" type="number" value={line.ordered_quantity} slotProps={{ htmlInput: { min: 1, step: 1, "aria-label": t("purchaseOrders.quantityFor", { product: line.product_name }) } }} onChange={(event) => setLines((current) => current.map((item, position) => position === index ? { ...item, ordered_quantity: event.target.value } : item))} sx={{ width: 100 }} /> : line.ordered_quantity}</TableCell>
            <TableCell align="right">{line.expected_unit_cost === null || line.expected_unit_cost === undefined ? t("purchaseOrders.unknown") : amount(line.expected_unit_cost)}</TableCell>
            <TableCell>{line.expected_cost_source || "—"}</TableCell><TableCell align="right">{line.received_quantity}</TableCell><TableCell align="right">{line.outstanding_quantity}</TableCell>
            <TableCell sx={{ minWidth: 220 }}>{line.recommendation_reason || "—"}</TableCell>
            {editable && <TableCell><Button color="error" disabled={lines.length === 1 || busy} onClick={() => setLines((current) => current.filter((_, position) => position !== index))}>{t("purchaseOrders.remove")}</Button></TableCell>}
          </TableRow>)}</TableBody></Table>
        </TableContainer>

        <Stack spacing={1.25} sx={{ display: { xs: "flex", md: "none" } }}>
          {lines.map((line, index) => <Box key={line.public_id} sx={{ p: 1.5, border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
            <Typography fontWeight={900}>{line.product_name}</Typography><Typography color="text.secondary">{variant(line)}</Typography>
            {editable ? <TextField fullWidth sx={{ mt: 1 }} label={t("purchaseOrders.orderedQuantity")} type="number" value={line.ordered_quantity} slotProps={{ htmlInput: { min: 1, step: 1, "aria-label": t("purchaseOrders.quantityFor", { product: line.product_name }) } }} onChange={(event) => setLines((current) => current.map((item, position) => position === index ? { ...item, ordered_quantity: event.target.value } : item))} /> : <Typography mt={1}>{t("purchaseOrders.quantities", { ordered: line.ordered_quantity, received: line.received_quantity, outstanding: line.outstanding_quantity })}</Typography>}
            <Typography variant="body2" mt={1}>{t("purchaseOrders.unitCost")}: {line.expected_unit_cost === null || line.expected_unit_cost === undefined ? t("purchaseOrders.unknown") : amount(line.expected_unit_cost)}</Typography>
            {line.recommendation_reason && <Typography variant="body2" color="text.secondary" mt={1}>{line.recommendation_reason}</Typography>}
            {editable && <Button color="error" disabled={lines.length === 1 || busy} onClick={() => setLines((current) => current.filter((_, position) => position !== index))}>{t("purchaseOrders.remove")}</Button>}
          </Box>)}
        </Stack>

        {(order.status === "ORDERED" || order.status === "PARTIALLY_RECEIVED") && <Alert severity="info" sx={{ mt: 2 }}>{t("purchaseOrders.receivingInfo")}</Alert>}
        <Divider sx={{ my: 2 }} />
        <Stack direction={{ xs: "column", sm: "row" }} gap={1}>
          {editable && <><Button variant="contained" onClick={save} disabled={busy}>{t("purchaseOrders.saveDraft")}</Button><Button color="success" variant="contained" onFocus={() => focusAIAction("purchase_orders.mark_ordered")} onMouseEnter={() => focusAIAction("purchase_orders.mark_ordered")} onClick={() => setDialog("order")} disabled={busy}>{t("purchaseOrders.markOrdered")}</Button></>}
          {CANCELLABLE.has(order.status) && <Button color="error" variant="outlined" onClick={() => setDialog("cancel")} disabled={busy}>{t("purchaseOrders.cancelOrder")}</Button>}
        </Stack>
      </CardContent></Card>

      <Dialog open={Boolean(dialog)} onClose={() => !busy && setDialog("")} fullWidth maxWidth="sm">
        <DialogTitle>{t(dialog === "order" ? "purchaseOrders.markOrdered" : "purchaseOrders.cancelOrder")}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t(dialog === "order" ? "purchaseOrders.markOrderedHelp" : "purchaseOrders.cancelHelp")}</DialogContentText>
          {dialog === "cancel" && <TextField autoFocus required fullWidth multiline minRows={2} label={t("purchaseOrders.cancelReason")} value={cancelReason} onChange={(event) => setCancelReason(event.target.value)} sx={{ mt: 2 }} />}
        </DialogContent>
        <DialogActions><Button onClick={() => setDialog("")} disabled={busy}>{t("common.cancel")}</Button><Button variant="contained" color={dialog === "cancel" ? "error" : "success"} onClick={transition} disabled={busy || (dialog === "cancel" && !cancelReason.trim())}>{t("common.confirm")}</Button></DialogActions>
      </Dialog>
    </MainLayout>
  );
}
